import { formatDateTime, formatNumber } from '../../../utils/formatUtils';
import {
  ADJUSTMENT_TYPES,
  MESSAGES,
  MOVEMENT_TYPE_LABELS,
  VALIDATION_RULES,
} from '../constants/inventoryArticlesConstants';

export const filterInventoryArticles = (articles = [], searchTerm = '', filters = {}) => {
  const normalized = searchTerm?.trim().toLowerCase();

  return articles.filter((article) => {
    const matchesSearch =
      !normalized ||
      article.code?.toLowerCase().includes(normalized) ||
      article.name?.toLowerCase().includes(normalized) ||
      article.description?.toLowerCase().includes(normalized) ||
      article.external_code?.toLowerCase().includes(normalized);

    const matchesStatus =
      !filters.status ||
      filters.status === 'all' ||
      (filters.status === 'active' && article.is_active) ||
      (filters.status === 'inactive' && !article.is_active);

    return matchesSearch && matchesStatus;
  });
};

export const validateInventoryArticle = (formData) => {
  const errors = {};

  VALIDATION_RULES.REQUIRED_FIELDS.forEach((field) => {
    if (!formData[field] || `${formData[field]}`.trim() === '') {
      errors[field] = MESSAGES.ERROR.REQUIRED_FIELD;
    }
  });

  if (formData.code && formData.code.length > VALIDATION_RULES.CODE_MAX_LENGTH) {
    errors.code = `Máximo ${VALIDATION_RULES.CODE_MAX_LENGTH} caracteres`;
  }

  if (formData.name && formData.name.length > VALIDATION_RULES.NAME_MAX_LENGTH) {
    errors.name = `Máximo ${VALIDATION_RULES.NAME_MAX_LENGTH} caracteres`;
  }

  if (
    formData.external_code &&
    formData.external_code.length > VALIDATION_RULES.EXTERNAL_CODE_MAX_LENGTH
  ) {
    errors.external_code = `Máximo ${VALIDATION_RULES.EXTERNAL_CODE_MAX_LENGTH} caracteres`;
  }

  return errors;
};

export const getUnitDescription = (units = [], uuidUnit) => {
  if (!uuidUnit) return '-';
  const unit = units.find((item) => item.uuid === uuidUnit);
  return unit?.description || '-';
};

export const getArticleLabel = (article) => {
  if (!article) return '-';
  return `${article.code} — ${article.name}`;
};

export const getWarehouseLabelFromRecord = (warehouse) => {
  if (!warehouse) return '';
  return warehouse.name || warehouse.code || '';
};

export const getWarehouseLabel = (warehousesByUuid = {}, uuid) => {
  if (!uuid) return '-';
  return getWarehouseLabelFromRecord(warehousesByUuid[uuid]) || '-';
};

export const toStockNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

export const getAvailableStock = (stock) => {
  const onHand = toStockNumber(stock?.quantity_on_hand);
  const reserved = toStockNumber(stock?.quantity_reserved);
  return stock?.available_quantity != null
    ? toStockNumber(stock.available_quantity)
    : onHand - reserved;
};

export const formatStockQuantity = (value) => formatNumber(value, { decimals: 3, fallback: '0' });

export const formatMovementType = (movementType) =>
  MOVEMENT_TYPE_LABELS[movementType] || movementType || '-';

export const formatMovementDate = (value) => formatDateTime(value);

export const getArticleStockApiErrorMessage = (
  error,
  fallbackMessage = 'No se pudo completar la operación'
) => {
  const data = error?.response?.data;

  if (!data) {
    return error?.message || fallbackMessage;
  }

  if (typeof data === 'string') {
    return data;
  }

  const candidates = [data.error, data.detail, data.message];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim().replace(/^Error de validación:\s*/i, '');
    }
  }

  return fallbackMessage;
};

export const getEstimatedResultingStock = (currentStock, adjustmentType, quantity) => {
  const current = toStockNumber(currentStock);
  const parsedQuantity = toStockNumber(quantity);

  if (!parsedQuantity) {
    return current;
  }

  return adjustmentType === ADJUSTMENT_TYPES.OUT
    ? current - parsedQuantity
    : current + parsedQuantity;
};

export const validateAdjustStockForm = (
  formData,
  currentStock,
  { requireWarehouse = false } = {}
) => {
  const errors = {};
  const quantity = toStockNumber(formData.quantity);
  const reason = formData.reason?.trim();

  if (requireWarehouse && !formData.uuid_warehouse) {
    errors.uuid_warehouse = MESSAGES.ERROR.REQUIRED_WAREHOUSE;
  }

  if (!quantity || quantity <= 0) {
    errors.quantity = MESSAGES.ERROR.INVALID_QUANTITY;
  }

  if (!reason) {
    errors.reason = MESSAGES.ERROR.REQUIRED_REASON;
  }

  if (
    formData.adjustmentType === ADJUSTMENT_TYPES.OUT &&
    quantity > 0 &&
    getEstimatedResultingStock(currentStock, ADJUSTMENT_TYPES.OUT, quantity) < 0
  ) {
    errors.quantity = MESSAGES.ERROR.NEGATIVE_STOCK;
  }

  return errors;
};

export const buildAdjustStockPayload = (articleUuid, warehouseUuid, formData) => {
  const quantity = toStockNumber(formData.quantity);
  const signedQuantity =
    formData.adjustmentType === ADJUSTMENT_TYPES.OUT ? -quantity : quantity;

  return {
    uuid_inventory_article: articleUuid,
    uuid_warehouse: warehouseUuid,
    quantity: signedQuantity,
    reference_type: 'manual_adjustment',
    observations: formData.reason.trim(),
  };
};
